import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { FileText, ScanText } from 'lucide-react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { jobHunterService, type JhCv, type JhCvSync } from '@/services/job-hunter.service';
import { Field, JhSelect, Notice } from '../ui';

export const syncSummary = (r: JhCvSync) => (r.changed.length ? `${r.changed.slice(0, 6).join(', ')}${r.changed.length > 6 ? ` and ${r.changed.length - 6} more` : ''}` : 'nothing new');

/** "Read profile from CV": pick a CV and whether it replaces what it states or only fills empty fields. */
export function ReadFromCv({ profileUid, dirty, onSynced }: { profileUid: string; dirty: boolean; onSynced: (r: JhCvSync) => void }) {
  const [open, setOpen] = useState(false);
  const [cvs, setCvs] = useState<JhCv[] | null>(null);
  const [cvUid, setCvUid] = useState('');
  const [mode, setMode] = useState<'replace' | 'fill'>('replace');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    jobHunterService.cvs(profileUid).then((rows) => {
      const usable = rows.filter((c) => (c.textChars ?? 1) > 0);
      setCvs(usable);
      setCvUid((cur) => cur || usable.find((c) => c.isDefault)?.cvUid || usable[0]?.cvUid || '');
    }).catch(() => setCvs([]));
  }, [open, profileUid]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await jobHunterService.syncProfileFromCv(cvUid, mode);
      if (r.error) toast(r.error, { icon: '⚠️', duration: 7000 });
      toast.success(r.changed.length ? `Profile updated from the CV: ${syncSummary(r)}` : 'The CV has nothing new for the profile');
      onSynced(r);
      setOpen(false);
    } catch { /* toasted */ } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="sm" variant="secondary" iconLeft={<ScanText size={14} />} onClick={() => setOpen(true)}>Read from CV</Button>
      <Modal open={open} onClose={() => setOpen(false)} title="Read the profile from a CV" widthClass="max-w-lg">
        {cvs === null ? <div className="h-32 animate-pulse rounded-xl bg-surface-alt" /> : !cvs.length ? (
          <p className="jh-muted">Add a CV with readable text first (CVs tab).</p>
        ) : (
          <div className="space-y-4">
            <Field label="CV">
              <JhSelect value={cvUid} onChange={setCvUid} options={cvs.map((c) => ({ value: c.cvUid, label: c.label, description: c.isDefault ? 'Default CV' : c.file?.name, icon: <FileText size={14} /> }))} />
            </Field>
            <div className="grid gap-2">
              {([['replace', 'Replace with the CV’s facts', 'Name, headline, contact, education, languages, experience, skills and links follow the CV.'], ['fill', 'Only fill empty fields', 'Keeps everything you typed; adds new skills and languages.']] as const).map(([value, title, hint]) => (
                <button key={value} type="button" onClick={() => setMode(value)} aria-pressed={mode === value}
                  className={clsx('rounded-xl border px-3.5 py-3 text-left transition', mode === value ? 'border-primary bg-primary-soft' : 'border-border hover:border-border-strong')}>
                  <div className="text-[13px] font-semibold">{title}</div>
                  <div className="mt-0.5 text-[12px] text-text-muted">{hint}</div>
                </button>
              ))}
            </div>
            <p className="text-[12px] text-text-muted">Work rights, availability, salary, saved answers and “never answer” are never changed — a CV doesn’t hold them.</p>
            {dirty && <Notice tone="warning" title="You have unsaved changes">Name, headline and truth-file edits on this page are replaced by what the CV says.</Notice>}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button loading={busy} disabled={!cvUid} iconLeft={<ScanText size={14} />} onClick={run}>Read profile</Button>
        </div>
      </Modal>
    </>
  );
}
